import type { ApplicationChoiceDto } from '../../Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import { CampusBrand, OnlineStudyBrand, VocationalSchoolBrand } from '~/Partials/GoogleTagManager/AnalyticsConstants';
import { TelemetryLogger } from '~/Utils/Logging/TelemetryLogger';
import type {
    GoogleTagManagerEcommerceAdd,
    GoogleTagManagerEcommerceBaseActionField,
    GoogleTagManagerEcommerceBeginCheckout,
    GoogleTagManagerEcommerceCheckout,
    GoogleTagManagerEcommerceCheckoutActionField,
    GoogleTagManagerEcommerceCheckoutOption,
    GoogleTagManagerEcommerceProduct,
    GoogleTagManagerEcommerceProductDetail,
    GoogleTagManagerEcommercePurchase,
    GoogleTagManagerEcommercePurchaseActionField,
    GoogleTagManagerEcommerceRemove,
} from './GoogleTagManagerModels';
import { GtmEventsBase } from './GtmEventsBase';

export class GtmCommerceEventsHandler extends GtmEventsBase {
    public static pushPurchase = (
        items: GoogleTagManagerEcommerceProduct[],
        actionField: GoogleTagManagerEcommercePurchaseActionField
    ) => {
        const ecommerce: GoogleTagManagerEcommercePurchase = {
            ...actionField,
            items,
        };

        GtmEventsBase.push({ event: 'purchase', ecommerce });
    };

    public static pushAddProducts = (
        items: GoogleTagManagerEcommerceProduct[],
        actionField: GoogleTagManagerEcommerceBaseActionField
    ) => {
        const ecommerce: GoogleTagManagerEcommerceAdd = {
            ...actionField,
            items,
        };
        GtmEventsBase.push({ event: 'add_to_cart', ecommerce });
    };

    public static pushRemoveProducts = (
        items: GoogleTagManagerEcommerceProduct[],
        actionField: GoogleTagManagerEcommerceBaseActionField
    ) => {
        const ecommerce: GoogleTagManagerEcommerceRemove = {
            ...actionField,
            items,
        };
        GtmEventsBase.push({ event: 'remove_from_cart', ecommerce });
    };

    public static pushBeginCheckout = (
        items: GoogleTagManagerEcommerceProduct[],
        actionField: GoogleTagManagerEcommerceBaseActionField
    ) => {
        const ecommerce: GoogleTagManagerEcommerceBeginCheckout = {
            ...actionField,
            items,
        };
        GtmEventsBase.push({ event: 'begin_checkout', ecommerce });
    };

    public static pushCheckout = (
        items: GoogleTagManagerEcommerceProduct[],
        actionField: GoogleTagManagerEcommerceCheckoutActionField
    ) => {
        const ecommerce: GoogleTagManagerEcommerceCheckout = {
            ...actionField,
            items,
        };
        GtmEventsBase.push({ event: 'checkout', ecommerce });
    };

    public static pushCheckoutOption = (checkout_step: number, option?: string) => {
        const eventName = 'checkout_option';
        const ecommerce: GoogleTagManagerEcommerceCheckoutOption = {
            checkout_option: {
                actionField: { checkout_step, option: option || eventName },
            },
        };

        GtmEventsBase.push({ event: eventName, ecommerce });
    };

    public static pushProductDetail = (newChoices: ApplicationChoiceDto[]) => {
        const ecommerce = {
            detail: {
                items: newChoices.map(GtmCommerceEventsHandler.createProductFromApplicationChoice),
                actionField: '',
            },
        } satisfies GoogleTagManagerEcommerceProductDetail;
        GtmEventsBase.push({ event: 'product_detail', ecommerce });
    };

    private static createProductFromApplicationChoice = (choice: ApplicationChoiceDto) => {
        const studyType =
            choice.level === VocationalSchoolBrand
                ? VocationalSchoolBrand
                : choice.variantCity === OnlineStudyBrand
                  ? OnlineStudyBrand
                  : CampusBrand;

        if (!choice.educationCategories) {
            TelemetryLogger.errorMessage(`Missing education categories for subject ${choice.id}`);
            choice.educationCategories = [];
        }

        return {
            item_id: choice.id,
            item_name: choice.name,
            affiliation: studyType,
            item_brand: choice.educationCategories.join(', '),
            item_category: choice.studyFormType,
            item_category2: choice.points,
            item_category3: choice.label,
            item_variant: choice.level,
            // default values set on a base of consultations with client
            price: choice.price ?? 100,
            quantity: 1,
        } satisfies GoogleTagManagerEcommerceProduct;
    };
}
